import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Mic, Film, Headphones, Users, Sparkles } from 'lucide-react';
import { useModal } from '../App';

const podcasts = [
  { name: "The Happy Pawdcast", host: "with Sonali Bendre" },
  { name: "The Filmy Hustle", host: "" },
  { name: "Two Boys One Cup", host: "" },
  { name: "Two Girls Two Cups", host: "" },
  { name: "Next In Marketing", host: "" },
  { name: "KalArt Shabd", host: "" },
  { name: "Evolve", host: "with Gita Hariani" },
];

const genres = [
  { name: "Romance", icon: Sparkles },
  { name: "Mythology", icon: Film },
  { name: "Horror", icon: Mic },
  { name: "Investigative Drama", icon: Users },
  { name: "Conversational Shows", icon: Headphones },
];

const AboutPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { openModal } = useModal();

  useEffect(() => {
    setIsLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const logoUrl = "https://customer-assets.emergentagent.com/job_podcast-launch-1/artifacts/bdhzg2gi_Rosepod%20logo%20white%20color.png";

  return (
    <div className="min-h-screen bg-[#04101C]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#04101C]/90 backdrop-blur-lg border-b border-[#00C4CC]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <img src={logoUrl} alt="Rosepod" className="h-10 md:h-12 w-auto" />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-[#F5FDFF]/70 hover:text-[#00E08F] transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#00C4CC]/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00E08F]/10 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1
              className={`text-4xl sm:text-5xl md:text-6xl font-bold text-[#F5FDFF] mb-6 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              About <span className="bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] bg-clip-text text-transparent">Us</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00C4CC] to-[#00E08F] mx-auto rounded-full" />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Legacy Section */}
          <div
            className={`mb-16 transition-all duration-700 delay-200 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-gradient-to-br from-[#00C4CC]/10 to-[#00E08F]/5 rounded-3xl p-8 md:p-12 border border-[#00C4CC]/20">
              <p className="text-[#F5FDFF]/90 text-lg md:text-xl leading-relaxed mb-6">
                <span className="text-[#00E08F] font-bold text-2xl">RosePod</span> is the digital content and podcasting arm of{' '}
                <span className="text-[#00C4CC] font-semibold">Rose Audiovisuals</span>, the acclaimed production house founded by{' '}
                <span className="text-[#FFF7AE] font-semibold">Goldie Behl in 1998</span>.
              </p>
              <p className="text-[#F5FDFF]/70 text-lg leading-relaxed">
                Backed by decades of storytelling excellence in film and television, RosePod brings the same creative depth and production integrity to the world of digital audio and video content.
              </p>
            </div>
          </div>

          {/* Our Shows Section */}
          <div
            className={`mb-16 transition-all duration-700 delay-300 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#F5FDFF] mb-8">
              Our <span className="bg-gradient-to-r from-[#00C4CC] to-[#00E08F] bg-clip-text text-transparent">Shows</span>
            </h2>
            <p className="text-[#F5FDFF]/70 text-lg leading-relaxed mb-8">
              We create and produce some of India&apos;s leading podcasts across categories, collaborating with celebrated voices, industry experts, and influential creators. Our growing slate includes hit shows such as:
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {podcasts.map((podcast, index) => (
                <div
                  key={index}
                  className="group bg-[#F5FDFF]/5 rounded-xl p-4 border border-[#00C4CC]/10 hover:border-[#00E08F]/50 hover:bg-[#00E08F]/5 transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00C4CC] to-[#00E08F] flex items-center justify-center flex-shrink-0">
                      <Play size={16} className="text-[#04101C] fill-current ml-0.5" />
                    </div>
                    <div>
                      <p className="text-[#F5FDFF] font-semibold group-hover:text-[#00E08F] transition-colors">
                        {podcast.name}
                      </p>
                      {podcast.host && (
                        <p className="text-[#F5FDFF]/50 text-sm">{podcast.host}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Our Mission Section */}
          <div
            className={`mb-16 transition-all duration-700 delay-400 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#F5FDFF] mb-8">
              Powerful <span className="bg-gradient-to-r from-[#00C4CC] to-[#FFF7AE] bg-clip-text text-transparent">Stories</span>
            </h2>
            <p className="text-[#F5FDFF]/70 text-lg leading-relaxed mb-8">
              At its core, RosePod is a home for powerful stories. We develop original and creator-led content across fiction and non-fiction, with a strong focus on building multi-season formats that encourage long-term listener engagement.
            </p>
            
            {/* Genres */}
            <div className="flex flex-wrap gap-3">
              {genres.map((genre, index) => {
                const Icon = genre.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5FDFF]/5 border border-[#00C4CC]/20 hover:border-[#00E08F]/50 transition-colors"
                  >
                    <Icon size={16} className="text-[#00E08F]" />
                    <span className="text-[#F5FDFF]/80 text-sm font-medium">{genre.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* End-to-End Section */}
          <div
            className={`transition-all duration-700 delay-500 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-gradient-to-r from-[#00C4CC]/20 via-[#00E08F]/10 to-[#FFF7AE]/10 rounded-3xl p-8 md:p-12 border border-[#00E08F]/20">
              <h2 className="text-2xl md:text-3xl font-bold text-[#F5FDFF] mb-6">
                End-to-End <span className="bg-gradient-to-r from-[#00E08F] to-[#FFF7AE] bg-clip-text text-transparent">Ecosystem</span>
              </h2>
              <p className="text-[#F5FDFF]/80 text-lg leading-relaxed mb-8">
                From concept development to scripting, production, distribution, and brand integration, RosePod offers an end-to-end podcasting ecosystem.
              </p>
              
              {/* Services Pills */}
              <div className="flex flex-wrap gap-3">
                {['Concept Development', 'Scripting', 'Production', 'Distribution', 'Brand Integration'].map((service, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 rounded-full bg-[#04101C]/50 text-[#00E08F] text-sm font-medium border border-[#00E08F]/30"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div
            className={`mt-16 text-center transition-all duration-700 delay-600 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h3 className="text-2xl font-bold text-[#F5FDFF] mb-4">Ready to tell your story?</h3>
            <p className="text-[#F5FDFF]/60 mb-8">Let&apos;s create something amazing together.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] text-[#04101C] font-bold text-lg hover:shadow-[0_0_40px_rgba(0,224,143,0.5)] transition-all duration-300 hover:scale-105"
            >
              Start Your Podcast
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#F5FDFF]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#F5FDFF]/40 text-sm">
            © 2025 Rosepod. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
